import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  HotelApproveState,
  GetHotelsParams,
  HotelApprovalRequest,
} from "@/types/admin/hotel-approve-type";
import { propertyReviewService } from "@/services/system-admin/property-review.service";
import {
  HotelRateSettings,
  HotelRateSettingsCreate,
  HotelRateSettingsUpdate,
} from "@/types/admin/hotel-rate-settings";

interface ExtendedHotelApproveState extends HotelApproveState {
  rateSettings: HotelRateSettings | null;
  rateSettingsLoading: boolean;
  hotelStatus: number | null;
}

const initialState: ExtendedHotelApproveState = {
  hotels: [],
  allHotels: [],
  approvedHotels: [],
  hotelDetail: null,
  loading: false,
  detailLoading: false,
  approvalLoading: false,
  error: null,
  totalCount: 0,
  totalApprovedCount: 0,
  totalRejectedCount: 0,
  totalActiveCount: 0,
  totalTodayApprovedCount: 0,
  pendingCount: 0,
  approvedTodayCount: 0,
  currentPage: 1,
  pageSize: 20,
  rateSettings: null,
  rateSettingsLoading: false,
  hotelStatus: null,
};

export const fetchApprovedHotels = createAsyncThunk(
  "hotelApprove/fetchApprovedHotels",
  async (params: {
    status?: number;
    page?: number;
    pageSize?: number;
    sortColumn?: string;
    searchKeyword?: string;
    sortOrder?: string;
  }) => {
    const response = await propertyReviewService.getApprovedHotels(params);
    return { ...response, page: params.page, pageSize: params.pageSize };
  },
);

export const fetchAllHotels = createAsyncThunk(
  "hotelApprove/fetchAllHotels",
  async () => {
    const response = await propertyReviewService.getHotels({
      page: 1,
      pageSize: 1000,
    });
    return response;
  },
);

export const fetchHotels = createAsyncThunk(
  "hotelApprove/fetchHotels",
  async (params: GetHotelsParams) => {
    const response = await propertyReviewService.getHotels(params);
    return response;
  },
);

export const fetchHotelDetail = createAsyncThunk(
  "hotelApprove/fetchHotelDetail",
  async (id: number) => {
    const response = await propertyReviewService.getHotelById(id);
    return response;
  },
);

export const approveHotel = createAsyncThunk(
  "hotelApprove/approveHotel",
  async ({
    id,
    approvalData,
  }: {
    id: number;
    approvalData: HotelApprovalRequest;
  }) => {
    const response = await propertyReviewService.approveHotel(id, approvalData);
    return response;
  },
);

export const fetchHotelRateSettings = createAsyncThunk(
  "hotelApprove/fetchHotelRateSettings",
  async ({ hotelId, baseValue }: { hotelId: number; baseValue: number }) => {
    const response = await propertyReviewService.getHotelRateSettings(
      hotelId,
      baseValue,
    );
    return response;
  },
);

export const createHotelRateSettings = createAsyncThunk(
  "hotelApprove/createHotelRateSettings",
  async (data: HotelRateSettingsCreate) => {
    const response = await propertyReviewService.createHotelRateSettings(data);
    return response;
  },
);

export const updateHotelRateSettings = createAsyncThunk(
  "hotelApprove/updateHotelRateSettings",
  async ({ id, data }: { id: number; data: HotelRateSettingsUpdate }) => {
    const response = await propertyReviewService.updateHotelRateSettings(
      id,
      data,
    );
    return response;
  },
);
export const setPendingHotel = createAsyncThunk(
  "hotelApprove/setPendingHotel",
  async ({
    id,
    data,
  }: {
    id: number;
    data: { status: number; reason: string };
  }) => {
    const response = await propertyReviewService.setHotelPendingStatus(
      id,
      data,
    );
    return response;
  },
);
export const fetchHotelStatus = createAsyncThunk(
  "hotelApprove/fetchHotelStatus",
  async (hotelId: number) => {
    const response = await propertyReviewService.getHotelStatus(hotelId);
    return response;
  },
);

const hotelApproveSlice = createSlice({
  name: "hotelApprove",
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearHotelDetail: (state) => {
      state.hotelDetail = null;
    },
    clearRateSettings: (state) => {
      state.rateSettings = null;
    },
    clearHotelStatus: (state) => {
      state.hotelStatus = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchApprovedHotels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApprovedHotels.fulfilled, (state, action) => {
        state.loading = false;
        // API returns { HotelAppriveList: [...], paging: null }
        const hotelList = action.payload.HotelAppriveList || [];
        state.approvedHotels = hotelList;
        // Update pagination state
        state.currentPage = action.payload.page || 1;
        state.pageSize = action.payload.pageSize || 20;
        // Extract counts from the first hotel item
        if (hotelList.length > 0) {
          const firstHotel = hotelList[0];
          state.totalCount = firstHotel.totalCount || 0;
          state.totalApprovedCount = firstHotel.totalApprovedCount || 0;
          state.totalRejectedCount = firstHotel.totalRejectedCount || 0;
          state.totalActiveCount = firstHotel.totalActiveCount || 0;
          state.totalTodayApprovedCount =
            firstHotel.totalTodayApprovedCount || 0;
        }
      })
      .addCase(fetchApprovedHotels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch approved hotels";
      })
      .addCase(fetchHotels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHotels.fulfilled, (state, action) => {
        state.loading = false;
        state.hotels = action.payload.result.items;
        state.totalCount = action.payload.result.totalCount;
        state.currentPage = action.payload.result.page;
        state.pageSize = action.payload.result.pageSize;
      })
      .addCase(fetchAllHotels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllHotels.fulfilled, (state, action) => {
        state.loading = false;
        state.allHotels = action.payload.result.items;
        state.totalCount = action.payload.result.totalCount;
        state.pendingCount = action.payload.result.items.filter(
          (hotel) => hotel.status === 1,
        ).length;
        state.approvedTodayCount = action.payload.result.items.filter(
          (hotel) => hotel.status === 2,
        ).length;
      })
      .addCase(fetchAllHotels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch all hotels";
      })
      .addCase(fetchHotels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch hotels";
      })
      .addCase(fetchHotelDetail.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      // CHANGE fetchHotelDetail.fulfilled back to preserve status:
      .addCase(fetchHotelDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        const existingStatus = state.hotelStatus;
        state.hotelDetail = action.payload;
        // If fetchHotelStatus already resolved, use that status value
        if (existingStatus !== null && state.hotelDetail) {
          state.hotelDetail.status = existingStatus;
        }
      })
      .addCase(fetchHotelDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.error.message || "Failed to fetch hotel details";
      })
      .addCase(approveHotel.pending, (state) => {
        state.approvalLoading = true;
        state.error = null;
      })
      .addCase(approveHotel.fulfilled, (state) => {
        state.approvalLoading = false;
      })
      .addCase(approveHotel.rejected, (state, action) => {
        state.approvalLoading = false;
        state.error = action.error.message || "Failed to approve hotel";
      })
      .addCase(fetchHotelRateSettings.pending, (state) => {
        state.rateSettingsLoading = true;
        state.error = null;
      })
      .addCase(fetchHotelRateSettings.fulfilled, (state, action) => {
        state.rateSettingsLoading = false;
        state.rateSettings = action.payload;
      })
      .addCase(fetchHotelRateSettings.rejected, (state, action) => {
        state.rateSettingsLoading = false;
        state.error = action.error.message || "Failed to fetch rate settings";
      })
      .addCase(createHotelRateSettings.pending, (state) => {
        state.rateSettingsLoading = true;
        state.error = null;
      })
      .addCase(createHotelRateSettings.fulfilled, (state, action) => {
        state.rateSettingsLoading = false;
        state.rateSettings = action.payload;
      })
      .addCase(createHotelRateSettings.rejected, (state, action) => {
        state.rateSettingsLoading = false;
        state.error = action.error.message || "Failed to create rate settings";
      })
      .addCase(updateHotelRateSettings.pending, (state) => {
        state.rateSettingsLoading = true;
        state.error = null;
      })
      .addCase(updateHotelRateSettings.fulfilled, (state, action) => {
        state.rateSettingsLoading = false;
        state.rateSettings = action.payload;
      })
      .addCase(updateHotelRateSettings.rejected, (state, action) => {
        state.rateSettingsLoading = false;
        state.error = action.error.message || "Failed to update rate settings";
      })
      .addCase(setPendingHotel.pending, (state) => {
        state.approvalLoading = true;
        state.error = null;
      })
      .addCase(setPendingHotel.fulfilled, (state) => {
        state.approvalLoading = false;
      })
      .addCase(setPendingHotel.rejected, (state, action) => {
        state.approvalLoading = false;
        state.error = action.error.message || "Failed to update hotel status";
      })
      .addCase(fetchHotelStatus.pending, (state) => {
        state.detailLoading = true;
      })
      .addCase(fetchHotelStatus.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.hotelStatus = action.payload.status;
        if (state.hotelDetail) {
          state.hotelDetail.status = action.payload.status;
        }
      })
      .addCase(fetchHotelStatus.rejected, (state) => {
        state.detailLoading = false;
      });
  },
});

export const {
  setPage,
  setPageSize,
  clearError,
  clearHotelDetail,
  clearHotelStatus,
  clearRateSettings,
} = hotelApproveSlice.actions;
export default hotelApproveSlice.reducer;
